import React, {FC, useState, useEffect, useMemo} from 'react'
import {Dropdown} from 'react-bootstrap'
import Select, {MultiValue} from 'react-select'
import Tooltip from 'react-bootstrap/Tooltip'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import axios from 'axios'
import {useDispatch, useSelector} from 'react-redux'
import {useAuth} from '../../../../modules/auth'
import {KTSVG} from '../../../../../_metronic/helpers'
import {CommonLoading} from '../../../../../Utils/commonLoading'
import {Form, Formik} from 'formik'
import {
  CustomerUnInvoicedCallsCurrentPage,
  CustomerUnInvoicedCallsFilter,
  CustomerUnInvoicedCallsRowsPerPage,
} from '../../../../redux/tableSlice/tableSlice'
import {RootState} from '../../../../redux/store'
import {useLanguages, useCommunicationTypes} from '../../../../hooks/useGlobalData'
import LazySelect from '../../../../components/LazySelect'

type Props = {
  setcall_log: any
  setCurrentPage: any
  setTotalPages: React.Dispatch<React.SetStateAction<number>>
  fetchcall_log: any
  searchQuery: any
  filterData: any
}

export function CustomerFilterDropdown({
  setcall_log,
  setCurrentPage,
  setTotalPages,
  fetchcall_log,
  searchQuery,
  filterData,
}: Props) {
  const [selectedRequestor, setSelectedRequestor] = useState<
    MultiValue<{value: string; label: string}>
  >([])
  const [selectedLanguages, setSelectedLanguages] = useState<
    MultiValue<{value: string; label: string}>
  >([])
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [subAccounts, setSubAccounts] = useState<any[]>([])
  const [selectedSubAccount, setSelectedSubAccount] = useState<any>()
  const [isLoadingSubAccounts, setIsLoadingSubAccounts] = useState(false)
  const {currentUser} = useAuth()
  const dispatch = useDispatch()
  const [showDropdownMenu, setShowDropdownMenu] = useState(false)
  const [isFilterApplied, setIsFilterApplied] = useState(false)
  const {rowsPerPage} = useSelector((state: any) => {
    return {
      rowsPerPage: state.table.CustomerUnInvoicedCalls[0].rowsPerPage,
    }
  })

  // Use global cached data for customers
  const {items: responseCustomer, loading: isFetchingCustomer} = useSelector(
    (state: RootState) => state.customers
  )
  
  // Use global cached data for languages and communication types
  const {data: languagesData, isLoading: isFetchingLanguage} = useLanguages()
  const {data: communicationTypesData, isLoading: isFetchingCommunication} = useCommunicationTypes()

  const customerOptions = useMemo(() => 
    responseCustomer?.map((d: any) => ({value: d.key ?? '', label: d.value ?? ''})) ?? []
  , [responseCustomer])
  
  // Alias for backward compatibility with existing code
  const requesterOptions = customerOptions
  
  const languageOptions = useMemo(() => 
    languagesData?.map((d: any) => ({value: d.key ?? '', label: d.value ?? ''})) ?? []
  , [languagesData])
  
  const communicationTypeOptions = useMemo(() => 
    communicationTypesData?.map((d: any) => ({value: d.key ?? '', label: d.value ?? ''})) ?? []
  , [communicationTypesData])
  
  // Alias for backward compatibility with existing code
  const communicationOptions = communicationTypeOptions

  const appointmentStatusOptions = [
    {value: '0', label: 'Open'},
    {value: '1', label: 'Ready to Assign'},
    {value: '2', label: 'Confirmed'},
    {value: '3', label: 'No Interpreter'},
    {value: '4', label: 'Abandoned'},
    {value: '5', label: 'Completed'},
    {value: '6', label: 'Canceled'},
    {value: '7', label: 'Submitted'},
  ]

  const meetingSummaryStatusOptions = [
    {value: '0', label: 'New'},
    {value: '1', label: 'Pending'},
    {value: '2', label: 'Approved'},
    {value: '3', label: 'Rejected'},
    {value: '-1', label: 'N/A'},
  ]

  useEffect(() => {
    const hasFilters =
      filterData?.customerList?.length > 0 ||
      filterData?.languageList?.length > 0 ||
      filterData?.communicationTypes?.length > 0 ||
      filterData?.startDate ||
      filterData?.endDate ||
      filterData?.appointmentStatusList?.length > 0 ||
      filterData?.meetingSummaryStatusList?.length > 0
    setIsFilterApplied(hasFilters)
  }, [filterData])

  // Fetch sub accounts when customers are selected
  const fetchSubAccounts = async (customers: any) => {
    setIsLoadingSubAccounts(true);
    try {
      const values = customers.map((item: any) => item.value);
      const url = `${process.env.REACT_APP_API_URL}/customer/sub-accounts/shortlist-multi`;
      const response = await axios.post<{ data: any[] }>(url, values);
      const list = response.data.data || [];
      const options = list.map(d => ({
        value: d.code,
        label: d.name,
        id: d.code,
      }));
      setSubAccounts([...options]);
    } catch (err) {
      console.error('Error fetching sub‑accounts:', err);
    } finally {
      setSelectedSubAccount(null);
      setIsLoadingSubAccounts(false);
    }
  };

  // Data is now loaded from global hooks above - these legacy functions are removed

  // const handleSubmit = async () => {
  //   const passingData = {
  //     customerList: selectedRequestor.map((option) => option.value.toString()),
  //     languageList: selectedLanguages.map((option) => option.label),
  //     startDate: startDate || undefined,
  //     callType: 50,
  //     endDate: endDate || undefined,
  //   }
  //   setFilterData(passingData)
  //   try {
  //     await fetchcall_log(passingData, {
  //       params: {
  //         page: 1,
  //         items_per_page: 10,
  //         ...searchQuery,
  //       },
  //     })
  //     setCurrentPage(1)
  //     setIsFilterApplied(true)
  //   } catch (error) {
  //     console.error(error)
  //   } finally {
  //     setShowDropdownMenu(false)
  //     setIsLoading(false)
  //   }
  // }

  const handleReset = async () => {
    dispatch(CustomerUnInvoicedCallsRowsPerPage(10))
    dispatch(CustomerUnInvoicedCallsCurrentPage(1))
    dispatch(
      CustomerUnInvoicedCallsFilter({
        customerList: [],
        languageList: [],
        communicationTypes: [],
        callStateList: [],
        startDate: null,
        endDate: null,
        companyCode: null,
        callType: 50,
        appointmentStatusList: [],
        meetingSummaryStatusList: [],
      })
    )
    setSelectedRequestor([])
    setSelectedLanguages([])
    setStartDate('')
    setEndDate('')
    setShowDropdownMenu(false)
    fetchcall_log(
      {
        customerList: [],
        languageList: [],
        communicationTypes: [],
        callStateList: [],
        startDate: null,
        endDate: null,
        companyCode: null,
        callType: 50,
        appointmentStatusList: [],
        meetingSummaryStatusList: [],
      },
      searchQuery,
      1,
      10
    )
    setIsFilterApplied(false)
  }

  return (
    <>
      <Dropdown onToggle={(nextShow) => setShowDropdownMenu(nextShow)}>
        <OverlayTrigger placement='top' overlay={<Tooltip id='tooltip-filter'>Filter</Tooltip>}>
          <Dropdown.Toggle
            variant='primary'
            id='dropdown-basic'
            className={`btn btn-icon btn-sm no-caret ${isFilterApplied ? 'btn-light-danger' : ''}`}
          >
            <KTSVG path='/media/icons/duotune/general/gen031.svg' className='svg-icon-primary' />
          </Dropdown.Toggle>
        </OverlayTrigger>
        {showDropdownMenu && (
          <Formik
            enableReinitialize
            initialValues={{
              ...filterData,
              customerList:
                requesterOptions.filter((d: any) =>
                  filterData?.customerList?.includes(d?.value.toString())
                ) ?? [],
              languageList:
                languageOptions.filter((d: any) => filterData?.languageList?.includes(d?.label)) ??
                [],
              communicationTypes:
                communicationOptions.filter((d: any) => filterData?.communicationTypes?.includes(d?.value)) ??
                [],
              appointmentStatusList:
                appointmentStatusOptions.filter((d: any) =>
                  filterData?.appointmentStatusList?.includes(d?.value)
                ) ?? [],
              meetingSummaryStatusList:
                meetingSummaryStatusOptions.filter((d: any) =>
                  filterData?.meetingSummaryStatusList?.includes(d?.value)
                ) ?? [],
            }}
            onSubmit={async (values, {setSubmitting, resetForm}) => {
              setSubmitting(true)
              const passingData = {
                customerList: values.customerList.map((option: any) => option.value.toString()),
                subAccounts: values.subaccounts?.map((option: any) => option.value.toString()),
                languageList: values.languageList.map((option: any) => option.label),
                communicationTypes: values.communicationTypes?.map((option: any) => option.value) || [],
                startDate: values.startDate || undefined,
                callType: 50,
                endDate: values.endDate || undefined,
                appointmentStatusList: values.appointmentStatusList?.map((option: any) => option.value) || [],
                meetingSummaryStatusList: values.meetingSummaryStatusList?.map((option: any) => option.value) || [],
              }
              try {
                dispatch(CustomerUnInvoicedCallsFilter({...passingData}))
                dispatch(CustomerUnInvoicedCallsCurrentPage(1))
                fetchcall_log(passingData, searchQuery, 1, rowsPerPage)
                const hasFilters =
                  passingData.customerList?.length > 0 ||
                  passingData.languageList?.length > 0 ||
                  passingData.communicationTypes?.length > 0 ||
                  passingData.startDate ||
                  passingData.endDate ||
                  passingData.appointmentStatusList?.length > 0 ||
                  passingData.meetingSummaryStatusList?.length > 0
                setIsFilterApplied(hasFilters)
              } catch (error) {
                console.error(error)
              } finally {
                setShowDropdownMenu(false)
                setIsLoading(false)
                setSubmitting(false)
              }
            }}
          >
            {({
              handleChange,
              handleBlur,
              setFieldValue,
              errors,
              values,
              touched,
              isValid,
              dirty,
              isSubmitting,
              handleSubmit,
              resetForm,
              ...formik
            }) => (
              <Form>
                <Dropdown.Menu className='p-4 w-350px' style={{minHeight: '100%'}}>
                  <div className='px-4 pb-4'>
                    <div className='fs-4 text-dark fw-semibolder'>Filter</div>
                  </div>

                  <div className='separator border-gray-200'></div>
                  <div className='card px-4 mt-4'>
                    <div className='row flex-column'>
                      {currentUser?.result?.userType !== 'CONSUMER' &&
                        currentUser?.result?.role !== 'User' && (
                          <div className='col-sm-12 col-md-12'>
                            <div className='mb-3'>
                              <div className='col-md-12'>
                                <div className='d-flex justify-content-between align-items-center'>
                                  <div>
                                    <label htmlFor='customerName' className='form-label fs-7 mb-1'>
                                      Customer(s)
                                    </label>
                                  </div>
                                  <div className='form-check form-check-custom form-check-solid form-check-sm mb-2'>
                                    <input
                                      className='form-check-input'
                                      type='checkbox'
                                      checked={
                                        values.customerList.length === requesterOptions.length
                                      }
                                      onChange={(e: any) => {
                                        if (e.target.checked) {
                                          setFieldValue('customerList', requesterOptions)
                                        } else {
                                          setFieldValue('customerList', [])
                                        }
                                      }}
                                    />
                                    <label className='form-check-label' htmlFor='flexRadioLg1'>
                                      All
                                    </label>
                                  </div>
                                </div>
                              </div>

                              <div className='row g-4 g-xl-4'>
                                <div className='col-sm-12 col-md-12 col-lg-12'>
                                  <div className='w-100'>
                                    <LazySelect
                                      name='customerList'
                                      options={!isFetchingCustomer ? requesterOptions : []}
                                      placeholder='Select Customer(s)'
                                      isMulti
                                      isLoading={isFetchingCustomer}
                                      value={values.customerList}
                                      onChange={(e: any) => {
                                        setFieldValue('customerList', e)
                                        fetchSubAccounts(e || [])
                                      }}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          
                        )}

                        {currentUser?.result?.userType === 'SYSTEM' && subAccounts && subAccounts.length > 0 && 
                        (
                          <div className='mb-3'>
                            <div className='col-md-12'>
                              <div className='d-flex justify-content-between align-items-center'>
                                <div>
                                  <label htmlFor='customerName' className='form-label fs-7 mb-1'>
                                    Sub Account Name
                                  </label>
                                </div>
                              </div>
                            </div>

                            <div className='row g-4 g-xl-4'>
                              <div className='col-sm-12 col-md-12 col-lg-12'>
                                
                                <div className='w-100'>
                                  <Select
                                    {...formik.getFieldProps('accounts')}
                                    className='react-select-styled react-select-solid react-select-sm'
                                    classNamePrefix='react-select'
                                    isMulti
                                    isLoading={isLoadingSubAccounts}
                                    options={subAccounts}
                                    placeholder='Select Sub Account'
                                    value={values.subaccounts}
                                    onChange={(selectedOption: any) => {
                                      setFieldValue(
                                        'subaccounts',
                                        selectedOption
                                      )
                                    }}
                                    styles={{
                                      control: (provided: any) => ({
                                        ...provided,
                                        width: '100%',
                                        maxHeight: '300px',
                                        overflow: 'auto',
                                        border: '1px solid #e4e6ef',
                                      }),
                                    }}
                                  />
                                </div>
                              </div>
                            </div>
                              
                          </div>
                        )
                      }

                      <div className='col-sm-12 col-md-12'>
                        <div className='mb-3'>
                          <div className='col-md-12'>
                            <div className='d-flex justify-content-between align-items-center'>
                              <div>
                                <label htmlFor='languages' className='form-label fs-7 mb-1'>
                                  Languages(s)
                                </label>
                              </div>
                              <div className='form-check form-check-custom form-check-solid form-check-sm mb-2'>
                                <input
                                  className='form-check-input'
                                  type='checkbox'
                                  checked={values.languageList.length === languageOptions.length}
                                  onChange={(e: any) => {
                                    if (e.target.checked) {
                                      setFieldValue('languageList', languageOptions)
                                    } else {
                                      setFieldValue('languageList', [])
                                    }
                                  }}
                                />
                                <label className='form-check-label' htmlFor='flexRadioLg1'>
                                  All
                                </label>
                              </div>
                            </div>
                          </div>

                          <div className='row g-4 g-xl-4'>
                            <div className='col-sm-12 col-md-12 col-lg-12'>
                              <div className='w-100'>
                                <LazySelect
                                  name='languageList'
                                  options={!isFetchingLanguage ? languageOptions : []}
                                  placeholder='Select Languages(s)'
                                  isMulti
                                  isLoading={isFetchingLanguage}
                                  value={values.languageList}
                                  onChange={(e: any) => setFieldValue('languageList', e)}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      {/* Communication Types filter */}
                      <div className='col-sm-12 col-md-12'>
                        <div className='mb-3'>
                          <div className='col-md-12'>
                            <div className='d-flex justify-content-between align-items-center'>
                              <div>
                                <label htmlFor='communicationTypes' className='form-label fs-7 mb-1'>
                                  Communication Type(s)
                                </label>
                              </div>
                              <div className='form-check form-check-custom form-check-solid form-check-sm mb-2'>
                                <input
                                  className='form-check-input'
                                  type='checkbox'
                                  checked={values.communicationTypes?.length === communicationOptions.length}
                                  onChange={(e: any) => {
                                    if (e.target.checked) {
                                      setFieldValue('communicationTypes', communicationOptions)
                                    } else {
                                      setFieldValue('communicationTypes', [])
                                    }
                                  }}
                                />
                                <label className='form-check-label' htmlFor='flexRadioLg1'>
                                  All
                                </label>
                              </div>
                            </div>
                          </div>

                          <div className='row g-4 g-xl-4'>
                            <div className='col-sm-12 col-md-12 col-lg-12'>
                              <div className='w-100'>
                                <LazySelect
                                  name='communicationTypes'
                                  options={!isFetchingCommunication ? communicationOptions : []}
                                  placeholder='Select Communication Type(s)'
                                  isMulti
                                  isLoading={isFetchingCommunication}
                                  value={values.communicationTypes}
                                  onChange={(e: any) => setFieldValue('communicationTypes', e)}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className='col-sm-12 col-md-12'>
                        <div className='mb-3'>
                          <div className='col-md-12'>
                            <div className='d-flex justify-content-between align-items-center'>
                              <div>
                                <label htmlFor='appointmentStatusList' className='form-label fs-7 mb-1'>
                                  Appointment Status
                                </label>
                              </div>
                              <div className='form-check form-check-custom form-check-solid form-check-sm mb-2'>
                                <input
                                  className='form-check-input'
                                  type='checkbox'
                                  checked={values.appointmentStatusList?.length === appointmentStatusOptions.length}
                                  onChange={(e: any) => {
                                    if (e.target.checked) {
                                      setFieldValue('appointmentStatusList', appointmentStatusOptions)
                                    } else {
                                      setFieldValue('appointmentStatusList', [])
                                    }
                                  }}
                                />
                                <label className='form-check-label' htmlFor='flexRadioLg1'>
                                  All
                                </label>
                              </div>
                            </div>
                          </div>

                          <div className='row g-4 g-xl-4'>
                            <div className='col-sm-12 col-md-12 col-lg-12'>
                              <div className='w-100'>
                                <Select
                                  className='react-select-styled react-select-solid react-select-sm'
                                  classNamePrefix='react-select'
                                  options={appointmentStatusOptions}
                                  placeholder='Select Appointment Status'
                                  isMulti
                                  value={values.appointmentStatusList}
                                  onChange={(e: any) => setFieldValue('appointmentStatusList', e)}
                                  styles={{
                                    control: (provided: any) => ({
                                      ...provided,
                                      width: '100%',
                                      border: '1px solid #e4e6ef',
                                    }),
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className='col-sm-12 col-md-12'>
                        <div className='mb-3'>
                          <div className='col-md-12'>
                            <div className='d-flex justify-content-between align-items-center'>
                              <div>
                                <label htmlFor='meetingSummaryStatusList' className='form-label fs-7 mb-1'>
                                  Meeting Summary Status
                                </label>
                              </div>
                              <div className='form-check form-check-custom form-check-solid form-check-sm mb-2'>
                                <input
                                  className='form-check-input'
                                  type='checkbox'
                                  checked={values.meetingSummaryStatusList?.length === meetingSummaryStatusOptions.length}
                                  onChange={(e: any) => {
                                    if (e.target.checked) {
                                      setFieldValue('meetingSummaryStatusList', meetingSummaryStatusOptions)
                                    } else {
                                      setFieldValue('meetingSummaryStatusList', [])
                                    }
                                  }}
                                />
                                <label className='form-check-label' htmlFor='flexRadioLg1'>
                                  All
                                </label>
                              </div>
                            </div>
                          </div>

                          <div className='row g-4 g-xl-4'>
                            <div className='col-sm-12 col-md-12 col-lg-12'>
                              <div className='w-100'>
                                <Select
                                  className='react-select-styled react-select-solid react-select-sm'
                                  classNamePrefix='react-select'
                                  options={meetingSummaryStatusOptions}
                                  placeholder='Select Meeting Summary Status'
                                  isMulti
                                  value={values.meetingSummaryStatusList}
                                  onChange={(e: any) => setFieldValue('meetingSummaryStatusList', e)}
                                  styles={{
                                    control: (provided: any) => ({
                                      ...provided,
                                      width: '100%',
                                      border: '1px solid #e4e6ef',
                                    }),
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className='fv-row'>
                        <div className='row'>
                          <div className='col-sm-6'>
                            <label htmlFor='startDate' className='form-label fs-7 mb-1'>
                              Start Date
                            </label>
                            <input
                              type='date'
                              className='form-control form-control-white form-select-sm custom-input-height'
                              id='startDate'
                              name='startDate'
                              value={values.startDate}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              placeholder='Enter Start Date'
                            />
                          </div>
                          <div className='col-sm-6'>
                            <label htmlFor='endDate' className='form-label fs-7 mb-1'>
                              End Date
                            </label>
                            <input
                              type='date'
                              className='form-control form-control-white form-select-sm custom-input-height'
                              id='endDate'
                              name='endDate'
                              value={values.endDate}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              placeholder='End Date'
                              max={new Date().toISOString().split('T')[0]}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className='text-end pt-5 px-4'>
                    <button
                      type='reset'
                      className='btn btn-sm btn-light me-2'
                      data-kt-menu-dismiss='true'
                      onClick={() => {
                        handleReset()
                        resetForm()
                      }}
                    >
                      Reset
                    </button>

                    <button
                      type='button'
                      className='btn btn-sm btn-primary'
                      data-kt-menu-dismiss='true'
                      onClick={() => handleSubmit()}
                      disabled={isSubmitting || !dirty || !isValid}
                    >
                      Apply
                    </button>
                  </div>
                </Dropdown.Menu>
              </Form>
            )}
          </Formik>
        )}
      </Dropdown>
      {isLoading && <CommonLoading />}
    </>
  )
}
